import {
  Card,
  CardContent,
  CardHead,
  CardTitle,
  SegmentedPicker,
} from '@payler/ui-components';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Divider,
  Flex,
  HStack,
  Icon,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FullWidthBlock, PageTitle } from '../layouts';
import React, { FC, Suspense } from 'react';
import styled from '@emotion/styled';
import { breakpoints } from '@payler/ui-theme';
import { useBalancesQuery, useSelectedContract } from '../hooks/contracts';
import { BalancesStrip } from '../components/Balances/BalancesStrip';
import { Helmet } from 'react-helmet-async';
import {
  BulkPayoutErrorContextProvider,
  useBulkPayoutErrorContext,
} from '../context/BulkPayoutErrorContext';
import { BulkRefundsTable } from '../tables/PaymentsTable/BulkRefundsTable';
import { BulkRefundForm } from '../forms/BulkRefundForm/BulkRefundForm';
import { useApi } from '../context/ApiContextProvider';
import { assert } from '@payler/utils';
import { Doc14Icon } from '@payler/ui-icons';
import { BulkPeriodDropdown } from '../components/filters/BulkPeriodDropdown';

const Wrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'form' 'table';
  grid-row-gap: 16px;
  @media (min-width: ${breakpoints.lg}) {
    display: grid;
    grid-template-columns: 320px 1fr;
    grid-template-areas: 'form table';
    align-items: flex-start;
    grid-column-gap: 16px;
  }
`;

const RETURN_MODE = ['single', 'mass'] as const;
type TReturnMode = (typeof RETURN_MODE)[number];

export const ReturnsPage: FC = () => {
  const { t } = useTranslation(['titles']);

  return (
    <BulkPayoutErrorContextProvider>
      <Helmet title={t('titles:returns')} />
      <Suspense fallback={null}>
        <ReturnsPageHeader />
      </Suspense>
      <ReturnsModeSwitcher />
      <BulkPayoutError />
      <Suspense fallback={null}>
        <Balances />
      </Suspense>

      <Wrap>
        <TableCard />
        <FormCard />
      </Wrap>
    </BulkPayoutErrorContextProvider>
  );
};

const ReturnsModeSwitcher: FC = () => {
  const { t } = useTranslation(['returns']);
  const { setBulkPayoutError, clearBulkPayoutError } =
    useBulkPayoutErrorContext();
  const { isBulkRefundOverPayoutAllowed } = useSelectedContract() || {};

  const optionsLoc: Record<TReturnMode, string> = {
    single: t('returns:single'),
    mass: t('returns:mass'),
  };
  const options = [optionsLoc.single, optionsLoc.mass];
  const massIndex = RETURN_MODE.indexOf('mass');

  React.useLayoutEffect(() => {
    if (!isBulkRefundOverPayoutAllowed) {
      setBulkPayoutError({ type: 'bulkPayoutsNotEnabled' });
    } else {
      clearBulkPayoutError();
    }
  }, [isBulkRefundOverPayoutAllowed, setBulkPayoutError, clearBulkPayoutError]);

  return (
    <Box w="full" my={4}>
      <SegmentedPicker
        options={options}
        onSelectionChange={() => {}}
        selection={massIndex}
      />
    </Box>
  );
};

const Balances = () => {
  const contract = useSelectedContract();
  const { data: balances } = useBalancesQuery(contract?.id);
  return <BalancesStrip balances={balances} />;
};

const TableCard: FC = () => {
  const { t } = useTranslation(['returns']);
  return (
    <FullWidthBlock gridArea="table">
      <Card>
        <CardHead>
          <HStack w="full" justifyContent="space-between" alignItems="center">
            <CardTitle>{t('returns:listTitle')}</CardTitle>
            <BulkPeriodDropdown />
          </HStack>
        </CardHead>
        <CardContent p={0}>
          <BulkRefundsTable />
        </CardContent>
      </Card>
    </FullWidthBlock>
  );
};

const FormCard: FC = () => {
  const { t } = useTranslation(['returns']);

  return (
    <FullWidthBlock gridArea="form">
      <Card>
        <CardHead>
          <CardTitle>{t('returns:formTitle')}</CardTitle>
        </CardHead>
        <CardContent>
          <DownloadTemplate />
        </CardContent>
        <Divider />
        <CardContent>
          <React.Suspense fallback={null}>
            <BulkRefundForm />
          </React.Suspense>
        </CardContent>
      </Card>
    </FullWidthBlock>
  );
};

const DownloadTemplate = () => {
  const { t } = useTranslation(['returns']);
  const api = useApi();
  const contractId = useSelectedContract()?.id;

  const handleDownloadButtonClick = async () => {
    assert(!!contractId, 'Contract id must be number');
    await api.downloadBulkRefundTemplate(contractId);
  };

  return (
    <HStack w="full" justifyContent="space-between">
      <Flex
        as="button"
        type="button"
        role="button"
        cursor="pointer"
        fontWeight={500}
        color="brands.500"
        onClick={handleDownloadButtonClick}
      >
        {t('returns:form.downloadTemplate')}
      </Flex>
      <Icon as={Doc14Icon} color="brands.500" />
    </HStack>
  );
};

export const BulkPayoutError = () => {
  const { errorMessage } = useBulkPayoutErrorContext();
  if (!errorMessage) return null;

  return (
    <Flex
      w="full"
      bgColor="red.100"
      border="1px solid"
      borderColor="red.100"
      borderRadius={1.5}
      p={2}
      mb={4}
    >
      {errorMessage}
    </Flex>
  );
};


const ReturnsPageHeader = () => {
  const { t } = useTranslation(['returns', 'common']);
  const contract = useSelectedContract();
  return (
    <HStack
      alignItems="flex-end"
      justifyContent="space-between"
      mb={{ base: 2, sm: 5 }}
    >
      <div>
        <Breadcrumb separator={<>&mdash;</>}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">
              {t('common:home')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Text}>{contract?.name}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink as={Text}>{t('returns:title')}</BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <PageTitle mb={0}>{t('returns:title')}</PageTitle>
      </div>
    </HStack>
  );
};

export default ReturnsPage;

